import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { API_URL } from '../../../config';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import getUniqueInvestorTypes from './getUniqueInvestorTypes';
import {
  persistInvestorFilters,
  readLegacyInvestorTypeCodes,
  readPersistedInvestorFilters,
  sortInvestorTypes,
  triggerFilterChange,
} from './utils';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import ToggleNeo from '../../../components/ui/toggle';
import { InputSearch } from '../../../components/ui/inputSearch';
import { ArrowDownWideNarrow, ArrowDownAZ } from 'lucide-react';
import { useMembershipAccess } from '../../../hooks/membership';


const InvestorsFilter = ({
  onFilterChange,
  filteredCount,
  totalResults,
  isLoading = false,
  onHydrated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const stripInvestmentInfo = (f, canUse) => ({
    ...f,
    onlyWithInvestmentsInfo: canUse ? f.onlyWithInvestmentsInfo : false,
  });
  const getFiltersSignature = (value = {}) =>
    JSON.stringify({
      selectedTypes: [...(value.selectedTypes || [])].sort(),
      searchKeyword: value.searchKeyword || '',
      searchCountry: value.searchCountry || '',
      industries: value.industries || [],
      onlyWithInvestmentsInfo: Boolean(value.onlyWithInvestmentsInfo),
    });

  const {
    canUseInvestmentInfoFilter: canAccessInvestmentInfo,
    loading,
  } = useMembershipAccess();

  const [filters, setFilters] = useState(() => readPersistedInvestorFilters(location.search));
  const onFilterChangeRef = useRef(onFilterChange);
  const hasReportedHydrationRef = useRef(false);
  const lastAppliedSignatureRef = useRef('');

  const reportHydrated = () => {
    if (hasReportedHydrationRef.current) {
      return;
    }

    hasReportedHydrationRef.current = true;
    onHydrated?.();
  };

  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  const setFiltersIfChanged = (nextFilters) => {
    setFilters((prevFilters) =>
      getFiltersSignature(prevFilters) === getFiltersSignature(nextFilters)
        ? prevFilters
        : nextFilters
    );
  };

  useEffect(() => {
    if (!canAccessInvestmentInfo && filters.onlyWithInvestmentsInfo) {
      const safe = { ...filters, onlyWithInvestmentsInfo: false };
      lastAppliedSignatureRef.current = getFiltersSignature(safe);
      setFiltersIfChanged(safe);
      triggerFilterChange(safe, onFilterChangeRef.current);
      persistInvestorFilters(safe);
    }
  }, [canAccessInvestmentInfo, filters]);

  const formatTotalCount = (totalResults) =>
    totalResults ? Number(totalResults).toLocaleString() : '';

  const [searchIndustry, setSearchIndustry] = useState('');
  const [investorTypes, setInvestorTypes] = useState([]);
  const [sortType, setSortType] = useState('priority');
  const industryInputRef = useRef(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const keywordInputRef = useRef(null);

  const [countries, setCountries] = useState([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef(null);
  const [searchCountry, setSearchCountry] = useState('');

  // Function to generate unique codes for investor types
  const generateInvestorTypeCode = (type, existingCodes) => {
    let code = type
      .split(' ')
      .map((word) => word[0].toLowerCase())
      .join('');
    // Ensure uniqueness
    let originalCode = code;
    let i = 1;
    while (existingCodes.has(code)) {
      code = originalCode + i;
      i++;
    }
    return code;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [countryDropdownRef]);

  useLayoutEffect(() => {
    if (loading) {
      return;
    }

    const safeFilters = stripInvestmentInfo(
      readPersistedInvestorFilters(location.search),
      canAccessInvestmentInfo
    );
    const signature = getFiltersSignature(safeFilters);

    setFiltersIfChanged(safeFilters);
    persistInvestorFilters(safeFilters);

    if (lastAppliedSignatureRef.current !== signature) {
      lastAppliedSignatureRef.current = signature;
      triggerFilterChange(safeFilters, onFilterChangeRef.current, false);
    }

    reportHydrated();
  }, [loading, canAccessInvestmentInfo, location.search]);

  useEffect(() => {
    if (loading) {
      return;
    }

    let cancelled = false;

    const fetchInvestorTypes = async () => {
      try {
        const uniqueTypes = await getUniqueInvestorTypes();
        if (cancelled) {
          return;
        }

        setInvestorTypes(uniqueTypes);

        const legacyCodes = readLegacyInvestorTypeCodes(location.search);
        if (!legacyCodes.length) {
          return;
        }

        const existingCodes = new Set();
        const codeToType = {};

        uniqueTypes.forEach((type) => {
          const code = generateInvestorTypeCode(type, existingCodes);
          existingCodes.add(code);
          codeToType[code] = type;
        });

        const decodedTypes = legacyCodes
          .map((code) => codeToType[code])
          .filter(Boolean);

        if (!decodedTypes.length) {
          return;
        }

        const reconciledFilters = stripInvestmentInfo(
          {
            ...readPersistedInvestorFilters(location.search),
            selectedTypes: decodedTypes,
          },
          canAccessInvestmentInfo
        );
        const signature = getFiltersSignature(reconciledFilters);

        setFiltersIfChanged(reconciledFilters);
        persistInvestorFilters(reconciledFilters);

        if (lastAppliedSignatureRef.current !== signature) {
          lastAppliedSignatureRef.current = signature;
          triggerFilterChange(reconciledFilters, onFilterChangeRef.current, false);
        }

        const params = new URLSearchParams(location.search);
        params.set('investorTypes', decodedTypes.join(','));
        const nextSearch = params.toString();
        const currentSearch = location.search.startsWith('?')
          ? location.search.slice(1)
          : location.search;

        if (nextSearch !== currentSearch) {
          navigate(
            {
              pathname: location.pathname,
              search: nextSearch ? `?${nextSearch}` : '',
            },
            { replace: true }
          );
        }
      } catch (error) {
        console.error('Error fetching investor types:', error);
      }
    };

    fetchInvestorTypes();

    return () => {
      cancelled = true;
    };
  }, [canAccessInvestmentInfo, loading, location.pathname, location.search, navigate]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/investors/filters?fields=country`,
          { withCredentials: true }
        );
        const countries = response.data?.country?.values ?? [];
        setCountries(countries);
      } catch (error) {
        console.error('Error fetching countries:', error);
        setCountries([]); // safe default
      }
    };
    fetchCountries();
  }, []);

  // Filter change handler
  const handleFilterChange = (updatedFilters) => {
    const clean = stripInvestmentInfo(updatedFilters, canAccessInvestmentInfo);

    if (getFiltersSignature(filters) === getFiltersSignature(clean)) {
      return;
    }

    setFiltersIfChanged(clean);
    lastAppliedSignatureRef.current = getFiltersSignature(clean);
    triggerFilterChange(clean, onFilterChangeRef.current);
    persistInvestorFilters(clean);

    // Update the URL parameters
    const params = new URLSearchParams();

    // Investor Types
    if (clean.selectedTypes.length > 0) {
      params.set('investorTypes', clean.selectedTypes.join(','));
    } else {
      params.delete('investorTypes');
    }

    // Country
    if (clean.searchCountry) {
      params.set('country', clean.searchCountry);
    } else {
      params.delete('country');
    }

    // Industries
    if (clean.industries.length > 0) {
      params.set('industries', clean.industries.join(','));
    } else {
      params.delete('industries');
    }

    // Keyword
    if (clean.searchKeyword) {
      params.set('keyword', clean.searchKeyword);
    } else {
      params.delete('keyword');
    }

    // Toggle
    if (clean.onlyWithInvestmentsInfo) {
      params.set('onlyWithInvestmentsInfo', 'true');
    } else {
      params.delete('onlyWithInvestmentsInfo');
    }

    // Build the new query string
    const newQueryString = params.toString();
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete('p');
    const currentQueryString = currentParams.toString();

    if (newQueryString === currentQueryString) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: newQueryString ? `?${newQueryString}` : '',
      },
      { replace: true }
    );
  };

  // Apply industry filter
  const applyIndustryFilter = () => {
    if (searchIndustry.trim() && filters.industries.length === 0) {
      const updatedIndustries = [searchIndustry.trim()];
      const updatedFilters = { ...filters, industries: updatedIndustries };
      handleFilterChange(updatedFilters);
      setSearchIndustry('');
    }
  };

  const applyKeywordFilter = () => {
    if (searchKeyword.trim()) {
      const updatedFilters = {
        ...filters,
        searchKeyword: searchKeyword.trim(),
      };
      handleFilterChange(updatedFilters);
      setSearchKeyword('');
    }
  };

  const handleKeywordKeyDown = (event) => {
    if (event.key === 'Enter') {
      applyKeywordFilter();
    }
  };

  // Remove industry from filter
  const removeIndustry = (industry) => {
    const updatedIndustries = filters.industries.filter((ind) => ind !== industry);
    const updatedFilters = { ...filters, industries: updatedIndustries };
    handleFilterChange(updatedFilters);
  };

  // Enter key handler
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      applyIndustryFilter();
    }
  };

  // Outside click handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        industryInputRef.current &&
        !industryInputRef.current.contains(event.target)
      ) {
        applyIndustryFilter();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchIndustry]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        keywordInputRef.current &&
        !keywordInputRef.current.contains(event.target)
      ) {
        applyKeywordFilter();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchKeyword]);

  return (
    <>
      <div className="px-2">
        <div className="mt-4">
          <span className="font-bold">
            {t('pageFindInvestors.fltrtotRslts')}
          </span>{' '}
          {isLoading ? (
           <span className="inline-flex items-center align-middle ml-1">
             <span className="animate-spin rounded-full h-4 w-4 border-2 border-stone-300 border-t-stone-900" />
           </span>
         ) : (
           formatTotalCount(totalResults)
         )}
        </div>
        {/* Updated block for the toggle with tooltip */}
        <div className="form-control mt-2 -ml-1 group relative">
  <label className="label cursor-pointer flex items-center">
    <span
      className={`label-text ${!canAccessInvestmentInfo ? 'opacity-50' : ''}`}
    >
      {t('pageFindInvestors.invinfobtn')}
    </span>

    <ToggleNeo
      id="investmentsInfo"
      size="sm"
      color="secondary"
      className="ml-2"
      checked={canAccessInvestmentInfo && filters.onlyWithInvestmentsInfo}
      disabled={!canAccessInvestmentInfo}
      onChange={(next) => {
        if (canAccessInvestmentInfo) {
          const updatedFilters = {
            ...filters,
            onlyWithInvestmentsInfo: next,
          };
          handleFilterChange(updatedFilters);
        }
      }}
    />
  </label>

  {!canAccessInvestmentInfo && (
    <div className="tooltip-content absolute top-1/2 -translate-y-1/2 left-full ml-2 px-3 py-2 bg-stone-700 text-xs text-white rounded-lg shadow-lg hidden group-hover:block z-10">
      {t('pageFindInvestors.requires1')}
    </div>
  )}
</div>

        <div className="mt-2 flex items-center">
          <label className="block mr-4 text-sm font-medium">
            {t('pageFindInvestors.fltrinvType')}
          </label>
          <div className="flex items-center">
            <ArrowDownWideNarrow strokeWidth={1.25}
              onClick={() => setSortType('priority')}
              className={`mr-1 cursor-pointer ${sortType === 'priority' ? 'opacity-100' : 'opacity-50'}`}
              style={{ width: '18px', height: '18px' }}
            />
            <ArrowDownAZ strokeWidth={1.25}
              onClick={() => setSortType('alphabetical')}
              className={`mr-1 cursor-pointer ${sortType === 'alphabetical' ? 'opacity-100' : 'opacity-50'}`}
              style={{ width: '18px', height: '18px' }}
            />
            <Button
              onClick={() =>
                handleFilterChange({ ...filters, selectedTypes: [] })
              }
              variant="link"
              size="xs"
              className={`text-xs py-1 px-2 text-red-500 hover:text-red-700 ${filters.selectedTypes.length ? 'visible' : 'invisible'
                }`}
            >
              {t('app:other.t1')}
            </Button>
          </div>
        </div>

        <div className="mt-5 max-h-40 overflow-y-auto">
          {sortInvestorTypes(investorTypes, sortType).map((type, index) => (
            <div key={index} className="flex items-center mb-1">
              <Checkbox
                checked={filters.selectedTypes.includes(type)}
                onCheckedChange={(checked) =>
                  handleFilterChange({
                    ...filters,
                    selectedTypes: checked
                      ? [...filters.selectedTypes, type]
                      : filters.selectedTypes.filter((t) => t !== type),
                  })
                }
                id={`investorType-${index}`}
                className="mr-2"
              />
              <label htmlFor={`investorType-${index}`} className="text-sm">
                {type}
              </label>
            </div>
          ))}

        </div>

        <div className="mt-5">
          <div className="mt-5">
            <label className="block mb-2 text-sm font-medium">
              {t('pageFindInvestors.fltrinvSearchKwrd')}
            </label>
            <Input
              selectedValues={filters.searchKeyword ? [filters.searchKeyword] : []}
              onAddValue={(value) => {
                handleFilterChange({ ...filters, searchKeyword: value });
              }}
              onRemoveValue={() =>
                handleFilterChange({ ...filters, searchKeyword: "" })
              }
              placeholder={t("pageFindInvestors.fltrinvSearchKwrdPlaceholder")}
            />
          </div>
          <div className="mt-5">
            <label className="block mb-2 text-sm font-medium">
              {t("pageFindInvestors.fltrinvSearchOffice")}
            </label>
            <InputSearch
              options={countries}
              selectedValue={filters.searchCountry}
              showCountryFlags
              onChange={(value) => {
                handleFilterChange({ ...filters, searchCountry: value });
              }}
              placeholder={t("pageFindInvestors.fltrinvSearchOfficePlaceholder")}
            />
          </div>
          <div className="mt-5 mb-5">
            <div className="mt-5">
              <label className="block mb-2 text-sm font-medium">
                {t('pageFindInvestors.fltrinvSearchIndustry')}
              </label>
              <Input
                selectedValues={filters.industries}
                onAddValue={(value) => {
                  const updatedIndustries = [...filters.industries, value];
                  handleFilterChange({ ...filters, industries: updatedIndustries });
                }}
                onRemoveValue={(value) => {
                  const updatedIndustries = filters.industries.filter((ind) => ind !== value);
                  handleFilterChange({ ...filters, industries: updatedIndustries });
                }}
                placeholder={t("pageFindInvestors.fltrinvSearchIndustryPlaceholder")}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvestorsFilter;
